import { z } from "zod";

import {
  AUTHOR_SEGMENT,
  isReservedStorefrontSegment,
  POST_INDEX_SEGMENT,
} from "../../page-builder/reserved-paths";

/**
 * Permalinks — the locked cross-layer contract (feature: permalinks). The
 * operator form and the operator-api PATCH validation both derive from this.
 * Authored from docs/features/permalinks/product.md (AC-01, AC-02).
 */

export const PERMALINK_PAGE_TYPES = [
  "tour",
  "destination",
  "activity",
  "blog",
  "postCategory",
  "postAuthor",
  "travelerLogin",
] as const;
export type PermalinkPageType = (typeof PERMALINK_PAGE_TYPES)[number];

/** Today's live storefront addresses, so an agency that never saves sees no change (AC-01). */
export const DEFAULT_PERMALINKS: Readonly<Record<PermalinkPageType, string>> = {
  tour: "tours",
  destination: "destinations",
  activity: "tour-types",
  blog: POST_INDEX_SEGMENT,
  postCategory: "category",
  postAuthor: AUTHOR_SEGMENT,
  travelerLogin: "sign-in",
};

const PERMALINK_MAX_LENGTH = 50;

// AC-02: lowercase letters, numbers, single hyphens, none at either end.
const permalinkWordSchema = z
  .string()
  .min(1, "Add a word for this address")
  .max(PERMALINK_MAX_LENGTH, `Use ${PERMALINK_MAX_LENGTH} characters or fewer`)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Use lowercase letters, numbers and hyphens",
  );

const wordFields = Object.fromEntries(
  PERMALINK_PAGE_TYPES.map((type) => [type, permalinkWordSchema]),
) as Record<PermalinkPageType, typeof permalinkWordSchema>;

/**
 * The full set of words. Cross-field rules:
 *  - AC-02: no two page types share a word.
 *  - AC-02: a reserved storefront segment is allowed only as that page type's
 *    own default. The seven defaults are themselves reserved (they are real
 *    routes), so this also stops one type taking another type's default.
 * Clashes with the agency's own pages and posts (also AC-02) need the
 * agency's data and are checked by operator-api, not here.
 */
export const permalinksSchema = z
  .object(wordFields)
  .superRefine((links, ctx) => {
    const seen = new Map<string, PermalinkPageType>();
    for (const type of PERMALINK_PAGE_TYPES) {
      const word = links[type];
      if (seen.has(word)) {
        ctx.addIssue({
          code: "custom",
          path: [type],
          message: "Another page type already uses this word",
        });
      }
      seen.set(word, type);
      if (
        word !== DEFAULT_PERMALINKS[type] &&
        isReservedStorefrontSegment(word)
      ) {
        ctx.addIssue({
          code: "custom",
          path: [type],
          message: "This word is reserved by TripCart",
        });
      }
    }
  });
export type Permalinks = z.infer<typeof permalinksSchema>;

/**
 * Operator PATCH — partial; the page sends only changed words. operator-api
 * merges it onto the stored set and validates the merged result against
 * `permalinksSchema`, so the cross-field rules hold on the whole set.
 */
export const updatePermalinksSchema = z.object(wordFields).partial();
export type UpdatePermalinks = z.infer<typeof updatePermalinksSchema>;
